import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { CategoriaTO } from "src/app/data/schema/Courses/CategoriaTO";
import { Tag } from "src/app/data/schema/Courses/Tag";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UserCustomFieldTypeDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldTypeDTO";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { DataAnalysisService } from "src/app/data/service/data-analysis/data-analysis.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { PrivacyPoliceDialogComponent } from "src/app/shared/component/dialogs/privacy-police-dialog/privacy-police-dialog.component";
import { TermsOfUseDialogComponent } from "src/app/shared/component/dialogs/terms-of-use-dialog/terms-of-use-dialog.component";
import { UserCustomFieldsComponent } from "src/app/shared/component/user-custom-fields/user-custom-fields.component";
import { environment } from "src/environments/environment";

@Component({
    selector: "app-registrar-form",
    templateUrl: "./registrar-form.component.html",
    styleUrls: ["./registrar-form.component.scss"]
})
export class RegistrarFormComponent implements OnInit {
    nomeFormGroup: UntypedFormGroup;
    nascimentoFormGroup: UntypedFormGroup;
    emailFormGroup: UntypedFormGroup;
    termosUsoFormGroup: UntypedFormGroup;
    politicaPrivacidadeFormGroup: UntypedFormGroup;

    termosUso: string;
    politicaPrivacidade: string;
    isLoading: boolean;
    error: string;
    errorApi: any;
    success: string;
    cadastrado: boolean;
    minDate: Date;
    maxDate: Date;
    uploadingFiles = false;

    tags: Tag[];

    _validPassword: boolean;
    _password: string;

    TermsOfUseClicked = false;
    privacyPolicyClicked = false;
    userCustomFields$: BehaviorSubject<UserCustomFieldDTO[]>;

    get showUserCustomFieldsForm(): boolean {
        return this.userCustomFields$?.value.length > 0;
    }

    userId: string;

    @ViewChild("appUserCustomFields") userCustomFieldsComponent: UserCustomFieldsComponent;

    constructor(
        private translate: TranslateService,
        private formBuilder: UntypedFormBuilder,
        private coursesService: ApiCoursesService,
        private userService: ApiUserService,
        private authService: AuthService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        public _matDialog: MatDialog,
        private dataAnalysisService: DataAnalysisService
    ) {
        this.tags = new Array<Tag>();
        this.isLoading = false;
        this.error = null;
        this.success = null;
        this.cadastrado = false;
        this.userCustomFields$ = new BehaviorSubject<UserCustomFieldDTO[]>([]);
        this.minDate = new Date(1900, 1, 1);
        this.maxDate = new Date(Date.now());
    }

    ngOnInit(): void {
        if (this.authService.getCurrentSubscriptionId() == null)
            this.subscriptionService.setCurrentSubscriptionId(environment.default_subscription_id);

        this.termosUso = this.subscriptionService.currentSubscription?.settings?.termsOfUseUrl;

        this.politicaPrivacidade = this.subscriptionService.currentSubscription?.settings?.privacyPolicyUrl;
        this.coursesService.getCategorias().subscribe((data: CategoriaTO[]) => {
            data.forEach((element) => {
                this.tags.push(new Tag(element));
            });
        });

        this.userCustomFields$.next(this.subscriptionService.currentSubscription?.settings?.userCustomFields);

        this.nomeFormGroup = this.formBuilder.group({
            nome: ["", Validators.required],
            sobrenome: ["", Validators.required]
        });

        this.emailFormGroup = this.formBuilder.group({
            email: ["", [Validators.required, Validators.email]]
        });

        this.termosUsoFormGroup = this.formBuilder.group({
            aceitoTermos: ["", Validators.required]
        });

        this.politicaPrivacidadeFormGroup = this.formBuilder.group({
            concordoPoliticaPrivacidade: ["", Validators.required]
        });
    }

    selectMainTag(id: string) {
        const selectedTag = this.tags.filter((tag) => tag.id === id)[0];
        selectedTag.selected = !selectedTag.selected;

        for (let index = 0; index < selectedTag.subCategorias.length; index++) {
            const child = selectedTag.subCategorias[index];
            child.selected = false;
        }
    }

    selectChildrenTag(idParent: string, idChild: string) {
        const selectedParentTag = this.tags.filter((tag) => tag.id === idParent)[0];

        const selectedTag = selectedParentTag.subCategorias.filter((child) => child.id === idChild)[0];
        selectedTag.selected = !selectedTag.selected;
    }

    getSelectedTags() {
        return this.tags.filter((tag) => tag.selected);
    }

    onChange($event: MatSlideToggleChange, stepper: MatStepper) {
        if ($event.checked) {
            stepper.next();
        }
    }

    login() {
        this.isLoading = true;

        if (!this._validPassword) return;

        this.authService
            .loginPassword(this.emailFormGroup.value.email, this._password, this.authService.getCurrentSubscriptionId())
            .subscribe(
                () => {
                    this.router.navigate(["home", "destaques"]);
                    this.isLoading = false;
                },
                (error) => {
                    this.translate
                        .get("REGISTER.DONE.MESSAGES.AUTH_GENERIC_ERROR")
                        .subscribe((res) => (this.error = res));
                    this.isLoading = false;
                }
            );
    }

    cadastrarUsuario() {
        this.isLoading = true;
        this.error = null;
        this.errorApi = null;
        this.success = null;

        const tagsInteressesPrincipais = new Array<string>();

        this.tags
            .filter((tag) => tag.selected)
            .forEach((element) => {
                tagsInteressesPrincipais.push(element.nome);
            });

        const tagsInteressesDetalhes = new Array<string>();

        this.tags
            .filter((tag) => tag.selected)
            .forEach((element) => {
                element.subCategorias
                    .filter((child) => child.selected)
                    .forEach((e) => {
                        tagsInteressesDetalhes.push(e.nome);
                    });
            });

        if (!this._validPassword) return;

        const filefieldName = this.userCustomFields$.value
            .filter((x) => x.type == UserCustomFieldTypeDTO.File)
            ?.map((x) => x.fieldName);

        this.userService
            .create(
                this.nomeFormGroup.value.nome,
                this.nomeFormGroup.value.sobrenome,
                this.emailFormGroup.value.email,
                tagsInteressesPrincipais,
                tagsInteressesDetalhes,
                this._password,
                null,
                null,
                false,
                this.userCustomFieldsComponent?.exportValues() || null
            )
            .pipe(
                mergeMap((user) => {
                    this.userId = user.id;

                    this.dataAnalysisService.CompleteRegistration(
                        "APP",
                        this.nomeFormGroup.value.nome + " " + this.nomeFormGroup.value.sobrenome,
                        this.emailFormGroup.value.email,
                        true
                    );

                    return this.authService.loginPassword(
                        this.emailFormGroup.value.email,
                        this._password,
                        this.authService.getCurrentSubscriptionId()
                    );
                })
            )
            .subscribe(
                () => {
                    filefieldName?.forEach((field) => {
                        const file = this.userCustomFieldsComponent?.filesToUpload[field];
                        const last = filefieldName.indexOf(field) == filefieldName.length - 1;
                        if (file) {
                            this.uploadingFiles = true;
                            this.uploadFileCustomField(this.userId, file, field, last);
                        }
                    });

                    this.cadastrado = true;
                    this.translate.get("REGISTER.DONE.MESSAGES.CREATED").subscribe((res) => (this.success = res));
                    this.isLoading = false;
                },
                (error) => {
                    this.translate
                        .get("REGISTER.DONE.MESSAGES.CREATE_GENERIC_ERROR")
                        .subscribe((res) => (this.error = res));
                    if (error.status >= 400 && error.status <= 499) this.errorApi = error.error.errors;
                    this.isLoading = false;
                }
            );
    }

    validPassword($event: boolean): void {
        this._validPassword = $event;
    }

    password(event: string): void {
        this._password = event;
    }

    openTermsOfUseDialog(): void {
        this.TermsOfUseClicked = true;
        this._matDialog.open(TermsOfUseDialogComponent);
    }

    openPrivacyPoliceDialog(): void {
        this.privacyPolicyClicked = true;
        this._matDialog.open(PrivacyPoliceDialogComponent);
    }

    trimEmail(): void {
        this.emailFormGroup.controls.email.setValue(this.emailFormGroup.controls.email.value.trim());
    }

    public uploadFileCustomField(userId: string, input: HTMLInputElement, fieldKey: string, last: boolean): void {
        if (input.files.length > 0)
            this.userService.uploadFileCustomField(userId, input.files[0], fieldKey).subscribe(
                () => {
                    if (last) this.uploadingFiles = false;
                },
                () => {
                    if (last) this.uploadingFiles = false;
                }
            );
    }

    getPasswordRegex(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordRegex;
    }

    getPasswordCustomRules(): string {
        return this.subscriptionService.currentSubscription?.loginSettings?.defaultLoginOptions?.passwordCustomRules;
    }
}
